/**
 * unified-slider.js - FIXED VERSION
 * Fixed loop slider autoplay pause on user interaction issues
 * Key fixes:
 * 1. Fixed interaction event handling for draggable
 * 2. Improved user interaction state management
 * 3. Fixed autoplay restart timing
 * 4. Added proper drag detection threshold
 */

import { createSliderControls, createSliderProgressBar, createVisibilityDetection } from './slider-utils.js';
import { horizontalLoop } from './horizontal-loop.js';

/**
 * Creates a unified slider with configurable behavior modes
 * @param {Object} options - Configuration options
 * @returns {Object} Slider instance with standard API
 */
export function createSlider(options = {}) {
  const {
    containerSelector,
    slideSelector = ".slide",
    mode = 'linear',
    autoplay = {},
    responsive = {},
    direction = 'horizontal',
    speed = 1,
    dragThreshold = 50,
    easing = 'expo.inOut',
    duration = 1.6,
    debug = false,
    center = false,
    paddingRight = 0,
    stackSettings = {},
    marqueeSettings = {},
    animationDelay = 0,
    animationStagger = 0.1
  } = options;

  const debugLog = (...args) => {
    if (debug) {
      console.log(`[${mode.charAt(0).toUpperCase() + mode.slice(1)}Slider]`, ...args);
    }
  }; 

  // Default configurations
  const defaultAutoplayConfig = {
    enabled: false,
    delay: 3000,
    pauseOnHover: true,
    pauseOnInteraction: true,
    resumeOnLeave: true,
    pauseOnInvisible: true,
    direction: 'next'
  };

  const defaultResponsive = {
    enabled: false,
    activeBelow: null,
    activeAbove: null,
    resetOnBreakpoint: true
  };

  const defaultStackSettings = {
    transitionStyle: 'slide',
    kenBurns: false,
    kenBurnsScale: 1.1,
    ...stackSettings
  };

  const defaultMarqueeSettings = {
    reverse: false,
    pauseOnHover: true,
    draggable: false,
    ...marqueeSettings
  };

  const mergedAutoplayConfig = { ...defaultAutoplayConfig, ...autoplay };
  const mergedResponsive = { ...defaultResponsive, ...responsive };

  // Get DOM elements - accept either a selector string or an element
  const container = typeof containerSelector === 'string' 
    ? document.querySelector(containerSelector) 
    : containerSelector;
  if (!container) {
    console.warn(`Slider container not found: ${containerSelector}`);
    return null;
  }

  const slides = gsap.utils.toArray(slideSelector, container);
  if (!slides.length) {
    console.warn(`No slides found in container: ${containerSelector}`);
    return null;
  }

  debugLog('Initializing slider', {
    mode,
    slideCount: slides.length,
    stackTransition: defaultStackSettings.transitionStyle,
    marqueeReverse: defaultMarqueeSettings.reverse,
    marqueeDraggable: defaultMarqueeSettings.draggable
  });

  // State variables
  let currentIndex = 0;
  let isActive = false;
  let isStackTransitioning = false;
  let loop = null;
  let autoplayInterval = null;
  let userInteracting = false;
  let isContainerVisible = true;
  let isPageVisible = true;
  let visibilityController = null;
  let mmContext = [];
  let autoplayWasPaused = false;
  
  // NEW: Track drag state specifically
  let isDragging = false;
  let dragStartTime = 0;
  let interactionTimeout = null;

  // Ken Burns state (for stack mode with fade transition)
  let kenBurnsTimelines = [];
  let isFirstKenBurnsRun = true;

  // Event dispatcher
  const dispatchSlideChangeEvent = (index) => {
    const event = new CustomEvent('slideChange', {
      detail: { item: slides[index], index: index },
      bubbles: true
    });
    container.dispatchEvent(event);
    debugLog('SlideChange event dispatched:', { index });
  };

  // Enhanced autoplay logic for linear mode cycling
  const handleLinearModeAutoplay = (direction) => {
    if (mode !== 'linear') {
      if (direction === 'previous') {
        loop.previous({ duration: duration, ease: easing });
      } else {
        loop.next({ duration: duration, ease: easing });
      }

      return;
    }

    const currentSlideIndex = loop.current();

    if (direction === 'next') {
      if (currentSlideIndex >= slides.length - 1) {
        debugLog("Linear autoplay: cycling back to first slide");
        loop.toIndex(0, { duration: duration, ease: easing });
      } else {
        loop.next({ duration: duration, ease: easing });
      }
    } else {
      if (currentSlideIndex <= 0) {
        debugLog("Linear autoplay: cycling to last slide");
        loop.toIndex(slides.length - 1, { duration: duration, ease: easing });
      } else {
        loop.previous({ duration: duration, ease: easing });
      }
    }
  };

  // FIXED: Improved user interaction management
  const setUserInteracting = (interacting, source = 'unknown') => {
    const wasInteracting = userInteracting;
    userInteracting = interacting;
    
    debugLog(`User interaction changed: ${wasInteracting} -> ${interacting} (${source})`);
    
    if (interacting !== wasInteracting) {
      updateAutoplayState();
    }
  };

  // FIXED: Clear any pending interaction timeouts
  const clearInteractionTimeout = () => {
    if (interactionTimeout) {
      clearTimeout(interactionTimeout);
      interactionTimeout = null;
    }
  };

  // FIXED: Set interaction end with delay
  const setInteractionEndDelayed = (delay = 1000, source = 'unknown') => {
    clearInteractionTimeout();
    interactionTimeout = setTimeout(() => {
      debugLog(`Delayed interaction end triggered (${source})`);
      setUserInteracting(false, `delayed-${source}`);
    }, delay);
  };

  // Autoplay functions
  const startAutoplay = (advanceImmediately = false) => {
    if (!mergedAutoplayConfig.enabled || !isActive || slides.length <= 1) {
      debugLog("Autoplay not starting:", {
        enabled: mergedAutoplayConfig.enabled,
        isActive,
        slideCount: slides.length
      });
      return;
    }

    stopAutoplay();
    
    // If resuming after a pause, advance immediately to show activity
    if (advanceImmediately && loop) {
      debugLog("Autoplay resuming - advancing slide immediately");
      if (mode === 'marquee') {
        if (mergedAutoplayConfig.direction === 'previous') {
          loop.previous({ duration: 0 });
        } else {
          loop.next({ duration: 0 });
        }
      } else {
        handleLinearModeAutoplay(mergedAutoplayConfig.direction);
      }
    }
    
    debugLog("Starting autoplay with delay:", mergedAutoplayConfig.delay);

    const startDelay = animationDelay * 1000 + mergedAutoplayConfig.delay;

    autoplayInterval = setInterval(() => {
      if (!userInteracting && isContainerVisible && isPageVisible && loop) {
        debugLog("Autoplay tick - advancing slide");
        if (mode === 'marquee') {
          if (mergedAutoplayConfig.direction === 'previous') {
            loop.previous({ duration: 0 });
          } else {
            loop.next({ duration: 0 });
          }
        } else {
          handleLinearModeAutoplay(mergedAutoplayConfig.direction);
        }
      } else {
        debugLog("Autoplay tick - conditions not met:", {
          userInteracting,
          containerVisible: isContainerVisible,
          pageVisible: isPageVisible,
          hasLoop: !!loop
        });
      }
    }, startDelay);
  };

  const stopAutoplay = () => {
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      autoplayInterval = null;
      autoplayWasPaused = true;
      debugLog("Autoplay stopped (paused)");
    }
  };

  const shouldAutoplayRun = () => {
    return mergedAutoplayConfig.enabled &&
      isActive &&
      !userInteracting &&
      isContainerVisible &&
      isPageVisible;
  };

  const updateAutoplayState = () => {
    const shouldRun = shouldAutoplayRun();
    const isRunning = autoplayInterval !== null;

    debugLog(`Autoplay state update: shouldRun=${shouldRun}, isRunning=${isRunning}`);

    if (shouldRun && !isRunning) {
      debugLog("Conditions met - starting autoplay");
      // Advance immediately if resuming from a pause
      const shouldAdvance = autoplayWasPaused;
      autoplayWasPaused = false;
      startAutoplay(shouldAdvance);
    } else if (!shouldRun && isRunning) {
      debugLog("Conditions not met - stopping autoplay");
      stopAutoplay();
    }
  };

  // FIXED: Mode-specific configurations with proper draggable setup
  const getModeConfig = () => {
    const baseConfig = {
      paused: true,
      center: center,
      paddingRight: paddingRight,
      onChange: (item, index) => {
        currentIndex = index;
        dispatchSlideChangeEvent(index);
      }
    };

    // FIXED: Add draggable configuration with proper event handling
    const shouldEnableDraggable = mode !== 'marquee' || (mode === 'marquee' && defaultMarqueeSettings.draggable);
    
    const draggableConfig = shouldEnableDraggable ? {
      draggable: true,
      // FIXED: Add custom drag event handlers
      onDragStart: () => {
        debugLog("Drag started");
        isDragging = true;
        dragStartTime = Date.now();
        clearInteractionTimeout();
        setUserInteracting(true, 'drag-start');
      },
      onDrag: () => {
        // Keep dragging state active
        if (!isDragging) {
          isDragging = true;
          setUserInteracting(true, 'drag-active');
        }
      },
      onDragEnd: () => {
        debugLog("Drag ended");
        isDragging = false;
        const dragDuration = Date.now() - dragStartTime;
        
        // FIXED: Only resume autoplay after a significant delay for drag interactions
        if (mergedAutoplayConfig.resumeOnLeave) {
          const resumeDelay = Math.max(1500, mergedAutoplayConfig.delay * 0.3);
          debugLog(`Setting drag end delay: ${resumeDelay}ms`);
          setInteractionEndDelayed(resumeDelay, 'drag-end');
        }
      },
      onThrowUpdate: () => {
        // Keep interaction active during throw
        setUserInteracting(true, 'throw-update');
      },
      onThrowComplete: () => {
        debugLog("Throw completed");
        isDragging = false;
        
        if (mergedAutoplayConfig.resumeOnLeave) {
          const resumeDelay = Math.max(1000, mergedAutoplayConfig.delay * 0.2);
          debugLog(`Setting throw complete delay: ${resumeDelay}ms`);
          setInteractionEndDelayed(resumeDelay, 'throw-complete');
        }
      }
    } : { draggable: false };

    switch (mode) {
      case 'linear':
        return {
          ...baseConfig,
          ...draggableConfig,
          repeat: 0,
          snap: true,
          speed: speed
        };
      case 'loop':
        return {
          ...baseConfig,
          ...draggableConfig,
          repeat: -1,
          snap: true,
          speed: speed
        };
      case 'marquee':
        const marqueeConfig = {
          ...baseConfig,
          ...draggableConfig,
          repeat: -1,
          snap: false,
          speed: speed * 0.5, // Always positive speed
          paused: false,
          reversed: defaultMarqueeSettings.reverse // Pass reverse flag separately
        };
        debugLog('Marquee config:', marqueeConfig);
        return marqueeConfig;
      case 'stack':
        return {
          ...baseConfig,
          repeat: -1,
          snap: true,
          speed: speed * 1.5
        };
      default:
        return { ...baseConfig, ...draggableConfig };
    }
  };

  // Ken Burns helper functions
  const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
  const randomFloat = (min, max) => parseFloat((Math.random() * (max - min) + min).toFixed(2));

  /**
   * Initialize Ken Burns animation for an image element
   * @param {HTMLElement} img - Image element with ken-burns-img class
   * @param {number} index - Slide index
   */
  const initKenBurns = (img, index) => {
    if (!defaultStackSettings.kenBurns) return;

    // Kill existing timeline for this slide if any
    if (kenBurnsTimelines[index]) {
      kenBurnsTimelines[index].kill();
    }

    // First slide on initial run: start neutral (no visible jump)
    const isInitialFirstSlide = isFirstKenBurnsRun && index === 0;
    
    let originX, originY, startScale, endScale;
    
    if (isInitialFirstSlide) {
      // Neutral start - gentle zoom from center
      originX = 50;
      originY = 50;
      startScale = 1;
      endScale = defaultStackSettings.kenBurnsScale;
    } else {
      // Randomize transform origin (avoid edges to prevent white space)
      originX = randomInt(20, 80);
      originY = randomInt(20, 80);
      
      // Random zoom direction: in or out
      const zoomOut = Math.random() > 0.5;
      const targetScale = randomFloat(1.05, defaultStackSettings.kenBurnsScale);
      
      startScale = zoomOut ? targetScale : 1;
      endScale = zoomOut ? 1 : targetScale;
    }
    
    const transformOrigin = `${originX}% ${originY}%`;

    // Set the initial Ken Burns state
    gsap.set(img, {
      scale: startScale,
      transformOrigin: transformOrigin,
      force3D: true
    });

    // Create Ken Burns timeline (paused initially)
    const tl = gsap.timeline({ paused: true });
    
    // Ken Burns duration is autoplay delay + 500ms buffer for smooth transitions
    const kenBurnsDuration = (mergedAutoplayConfig.delay + 500) / 1000;
    
    tl.to(img, {
      scale: endScale,
      duration: kenBurnsDuration,
      ease: "power1.inOut",
      force3D: true
    });

    kenBurnsTimelines[index] = tl;
    debugLog('Ken Burns initialized for slide', index, { originX, originY, startScale, endScale });
  };

  /**
   * Start Ken Burns animation for a slide
   * @param {number} index - Slide index
   */
  const startKenBurns = (index) => {
    if (!defaultStackSettings.kenBurns || !kenBurnsTimelines[index]) return;
    kenBurnsTimelines[index].play();
    debugLog('Ken Burns started for slide', index);
  };

  /**
   * Stop Ken Burns animation for a slide
   * @param {number} index - Slide index
   */
  const stopKenBurns = (index) => {
    if (!defaultStackSettings.kenBurns || !kenBurnsTimelines[index]) return;
    kenBurnsTimelines[index].pause();
    debugLog('Ken Burns stopped for slide', index);
  };

  /**
   * Cleanup all Ken Burns timelines
   */
  const cleanupKenBurns = () => {
    kenBurnsTimelines.forEach(tl => {
      if (tl) tl.kill();
    });
    kenBurnsTimelines = [];
    isFirstKenBurnsRun = true;
    debugLog('Ken Burns timelines cleaned up');
  };

  // Stack mode setup with transition styles
  const setupStackMode = () => {
    if (mode !== 'stack') return;

    debugLog('Setting up stack mode layout with transition:', defaultStackSettings.transitionStyle, 'kenBurns:', defaultStackSettings.kenBurns);

    container.style.position = 'relative';
    container.style.overflow = 'hidden';

    // Initialize Ken Burns for ALL slides first if enabled (fade mode only)
    if (defaultStackSettings.kenBurns && defaultStackSettings.transitionStyle === 'fade') {
      slides.forEach((slide, index) => {
        const img = slide.querySelector('img.ken-burns-img');
        if (img) {
          initKenBurns(img, index);
        }
      });
    }

    slides.forEach((slide, index) => {
      gsap.set(slide, {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: index === 0 ? 10 : 1,
        opacity: index === 0 ? 1 : 0,
        x: 0,
        xPercent: 0,
        scale: defaultStackSettings.transitionStyle === 'scale' ? (index === 0 ? 1 : 0.9) : 1,
        rotationY: defaultStackSettings.transitionStyle === 'flip' ? (index === 0 ? 0 : 180) : 0
      });
    });

    // Start Ken Burns on first slide
    if (defaultStackSettings.kenBurns && defaultStackSettings.transitionStyle === 'fade') {
      startKenBurns(0);
      isFirstKenBurnsRun = false;
    }

    debugLog('Stack layout applied to', slides.length, 'slides');
  };

  // Enhanced stack transition animation with different styles
  const animateStackTransition = (fromIndex, toIndex, directionHint) => {
    if (fromIndex === toIndex) return;
    if (isStackTransitioning) {
      debugLog('Transition blocked - already animating');
      return false;
    }

    isStackTransitioning = true;
    debugLog('Animating stack transition from', fromIndex, 'to', toIndex, 'style:', defaultStackSettings.transitionStyle);

    const currentSlide = slides[fromIndex];
    const targetSlide = slides[toIndex];
    // Use explicit directionHint when provided (handles wrap-around correctly)
    const direction = directionHint != null ? directionHint : (toIndex > fromIndex ? 1 : -1);
    const transitionStyle = defaultStackSettings.transitionStyle;
    targetSlide.style.display = '';

    const staggeredDuration = duration + (animationStagger * Math.abs(toIndex - fromIndex));

    switch (transitionStyle) {
      case 'fade':
        // Stop Ken Burns on current slide
        stopKenBurns(fromIndex);

        // Re-initialize Ken Burns for next slide with fresh random values (while still hidden)
        if (defaultStackSettings.kenBurns) {
          const nextImg = targetSlide.querySelector('img.ken-burns-img');
          if (nextImg) {
            initKenBurns(nextImg, toIndex);
          }
        }

        // Crossfade: both slides transition opacity simultaneously
        gsap.set(targetSlide, { opacity: 0, zIndex: 10, x: 0 });
        gsap.set(currentSlide, { zIndex: 5 });

        // Start Ken Burns BEFORE slide becomes visible for smooth animation
        startKenBurns(toIndex);

        const fadeTimeline = gsap.timeline({
          onComplete: () => {
            gsap.set(currentSlide, { opacity: 0, zIndex: 1 });
            isStackTransitioning = false;
            debugLog('Fade transition completed');
          }
        });
        // Both fade out and fade in happen at the same time for crossfade effect
        fadeTimeline
          .to(currentSlide, { opacity: 0, duration: staggeredDuration, ease: easing }, 0)
          .to(targetSlide, { opacity: 1, duration: staggeredDuration, ease: easing }, 0);
        break;

      case 'scale':
        gsap.set(targetSlide, { scale: 0.8, opacity: 0, zIndex: 10, x: 0 });
        const scaleTimeline = gsap.timeline({
          onComplete: () => {
            gsap.set(currentSlide, { opacity: 0, zIndex: 1, scale: 1 });
            isStackTransitioning = false;
            debugLog('Scale transition completed');
          }
        });
        scaleTimeline
          .to(currentSlide, { scale: 1.1, opacity: 0, duration: staggeredDuration * 0.6, ease: easing })
          .to(targetSlide, { scale: 1, opacity: 1, duration: staggeredDuration * 0.6, ease: easing }, "-=0.2");
        break;

      case 'flip':
        gsap.set(targetSlide, { rotationY: direction > 0 ? 180 : -180, opacity: 1, zIndex: 10, x: 0 });
        const flipTimeline = gsap.timeline({
          onComplete: () => {
            gsap.set(currentSlide, { opacity: 0, zIndex: 1, rotationY: 0 });
            isStackTransitioning = false;
            debugLog('Flip transition completed');
          }
        });
        flipTimeline
          .to(currentSlide, { rotationY: direction > 0 ? -90 : 90, duration: staggeredDuration * 0.5, ease: easing })
          .set(currentSlide, { opacity: 0 })
          .set(targetSlide, { opacity: 1 })
          .to(targetSlide, { rotationY: 0, duration: staggeredDuration * 0.5, ease: easing });
        break;

      case 'slide':
      default:
        gsap.set(currentSlide, { x: 0, opacity: 1, zIndex: 5 });
        gsap.set(targetSlide, { x: direction > 0 ? '100%' : '-100%', opacity: 1, zIndex: 10 });
        gsap.to(targetSlide, {
          x: 0,
          duration: staggeredDuration,
          ease: easing,
          onComplete: () => {
            gsap.set(currentSlide, { opacity: 0, zIndex: 1, x: 0 });
            gsap.set(targetSlide, { zIndex: 10, x: 0 });
            isStackTransitioning = false;
            debugLog('Slide transition completed, current slide is now', toIndex);
          }
        });
        break;

      case 'mask':
        // Direction-aware mask reveal using clip-path inset
        // Next: wipe from right edge; Previous: wipe from left edge
        const maskFrom = direction > 0
          ? 'inset(0 100% 0 0)'   // fully clipped from right
          : 'inset(0 0 0 100%)';  // fully clipped from left
        const maskTo = 'inset(0 0% 0 0%)';

        gsap.set(targetSlide, {
          clipPath: maskFrom,
          opacity: 1,
          zIndex: 10,
          x: 0
        });
        gsap.set(currentSlide, { zIndex: 5 });

        const maskTimeline = gsap.timeline({
          onComplete: () => {
            gsap.set(currentSlide, { opacity: 0, zIndex: 1 });
            gsap.set(targetSlide, { clipPath: 'none', zIndex: 10 });
            isStackTransitioning = false;
            debugLog('Mask transition completed');
          }
        });
        maskTimeline.to(targetSlide, {
          clipPath: maskTo,
          duration: staggeredDuration,
          ease: easing
        });
        break;
    }
  };

  // Enhanced stack-specific dragging setup with dragThreshold
  const setupStackDragging = () => {
    // Disable dragging for fade or mask mode
    if (defaultStackSettings.transitionStyle === 'fade' || defaultStackSettings.transitionStyle === 'mask') {
      debugLog("Dragging disabled for " + defaultStackSettings.transitionStyle + " transition style");
      return;
    }

    if (typeof Draggable === 'undefined') {
      debugLog("Draggable not available for stack mode");
      return;
    }

    const dragOverlay = document.createElement('div');
    dragOverlay.className = 'slider-interaction-overlay';
    dragOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 20;
      background: transparent;
      cursor: grab;
      touch-action: pan-y pinch-zoom;
      user-select: none;
    `;

    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    container.appendChild(dragOverlay);

    let startIndex = currentIndex;
    let dragDirection = 0;
    let previewSlide = null;

    const stackDraggable = Draggable.create(dragOverlay, {
      type: 'x',
      allowNativeTouchScrolling: true,
      allowEventDefault: false,

      onPress: () => {
        if (!isActive) return false;

        startIndex = currentIndex;
        dragDirection = 0;
        previewSlide = null;
        dragOverlay.style.cursor = 'grabbing';

        // FIXED: Proper interaction handling for stack mode
        clearInteractionTimeout();
        setUserInteracting(true, 'stack-press');
        debugLog('Stack drag started from index', startIndex);
      },

      onDrag: function () {
        if (!isActive) return;

        const dragAmount = this.x;
        dragDirection = dragAmount > 0 ? 1 : -1;

        let previewIndex;
        if (dragDirection > 0) {
          previewIndex = (currentIndex + 1) % slides.length;
        } else {
          previewIndex = (currentIndex - 1 + slides.length) % slides.length;
        }

        const currentSlide = slides[currentIndex];
        previewSlide = slides[previewIndex];

        if (!currentSlide || !previewSlide) return;

        gsap.set(currentSlide, { x: 0, opacity: 1, zIndex: 5 });

        const slideWidth = container.offsetWidth;
        const dragProgress = Math.abs(dragAmount) / slideWidth;
        const clampedProgress = Math.min(dragProgress, 1);

        let previewX;
        if (dragDirection > 0) {
          previewX = `${100 * (1 - clampedProgress)}%`;
        } else {
          previewX = `${-100 * (1 - clampedProgress)}%`;
        }

        gsap.set(previewSlide, { x: previewX, opacity: 1, zIndex: 10 });
      },

      onRelease: function () {
        if (!isActive) return;

        const dragAmount = this.x;
        const shouldTransition = Math.abs(dragAmount) > dragThreshold;

        dragOverlay.style.cursor = 'grab';

        debugLog('Stack drag released:', { dragAmount, shouldTransition, threshold: dragThreshold, direction: dragDirection });

        if (shouldTransition && previewSlide) {
          const targetIndex = dragDirection > 0
            ? (currentIndex + 1) % slides.length
            : (currentIndex - 1 + slides.length) % slides.length;

          gsap.to(previewSlide, {
            x: 0,
            opacity: 1,
            duration: duration * 0.5,
            ease: easing,
            onComplete: () => {
              gsap.set(slides[currentIndex], { opacity: 0, zIndex: 1, x: 0 });
              gsap.set(previewSlide, { zIndex: 10, x: 0 });

              currentIndex = targetIndex;
              dispatchSlideChangeEvent(currentIndex);
              debugLog('Stack drag transition completed to index', currentIndex);
            }
          });
        } else {
          if (previewSlide) {
            gsap.to(previewSlide, {
              x: dragDirection > 0 ? '100%' : '-100%',
              duration: duration * 0.4,
              ease: easing,
              onComplete: () => {
                gsap.set(previewSlide, { opacity: 0, zIndex: 1 });
              }
            });
          }
        }

        gsap.set(dragOverlay, { x: 0 });

        // FIXED: Proper interaction end handling for stack mode
        if (mergedAutoplayConfig.resumeOnLeave) {
          const resumeDelay = Math.max(800, mergedAutoplayConfig.delay * 0.25);
          setInteractionEndDelayed(resumeDelay, 'stack-release');
        }
      }
    })[0];

    container._stackDraggable = stackDraggable;
    container._dragOverlay = dragOverlay;
  };

  // FIXED: Enhanced interaction setup with proper event handling
  const setupInteractionListeners = () => {
    debugLog("Setting up interaction listeners for autoplay:", {
      autoplayEnabled: mergedAutoplayConfig.enabled,
      pauseOnHover: mergedAutoplayConfig.pauseOnHover,
      pauseOnInteraction: mergedAutoplayConfig.pauseOnInteraction,
      mode: mode,
      marqueePauseOnHover: defaultMarqueeSettings.pauseOnHover
    });

    const handleInteractionStart = (source = 'unknown') => {
      debugLog(`Interaction started: ${source}`);
      clearInteractionTimeout();
      setUserInteracting(true, source);

      if (mode === 'marquee' && defaultMarqueeSettings.pauseOnHover && loop) {
        loop.pause();
        debugLog("Marquee paused on hover");
      }

      // For loop mode, we let the autoplay system handle pausing via userInteracting state
      // Don't pause the loop directly as it will continue as marquee when resumed
    };

    const handleInteractionEnd = (source = 'unknown', delay = 100) => {
      debugLog(`Interaction ended: ${source}`);
      if (mergedAutoplayConfig.resumeOnLeave) {
        setInteractionEndDelayed(delay, source);
      }

      if (mode === 'marquee' && defaultMarqueeSettings.pauseOnHover && loop) {
        loop.resume();
        debugLog("Marquee resumed");
      }

      // For loop mode, we let the autoplay system handle resuming via userInteracting state
      // The autoplay interval will restart when userInteracting becomes false
    };

    // FIXED: Always set up hover events if pauseOnHover is true (regardless of initial autoplay state)
    if (mergedAutoplayConfig.pauseOnHover) {
      let hoverTimeout = null;
      
      debugLog("Setting up hover events for autoplay pause");
      
      container.addEventListener('mouseenter', (e) => {
        debugLog("Mouse entered container", e.target);
        // Clear any pending mouseleave timeout
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }

        handleInteractionStart('hover');
      });
      
      container.addEventListener('mouseleave', (e) => {
        debugLog("Mouse left container", e.target);
        // Add small delay to prevent rapid hover on/off
        hoverTimeout = setTimeout(() => {
          handleInteractionEnd('hover', 200);
          hoverTimeout = null;
        }, 50);
      });
    }

    // FIXED: Separate marquee hover handling
    if (mode === 'marquee' && defaultMarqueeSettings.pauseOnHover && !mergedAutoplayConfig.enabled) {
      debugLog("Setting up hover events for marquee pause (no autoplay)");
      
      container.addEventListener('mouseenter', (e) => {
        debugLog("Mouse entered - pausing marquee");
        if (loop) {
          loop.pause();
        }
      });
      
      container.addEventListener('mouseleave', (e) => {
        debugLog("Mouse left - resuming marquee");
        if (loop) {
          loop.resume();
        }
      });
    }

    // FIXED: Touch events with proper delays
    if (mergedAutoplayConfig.pauseOnInteraction) {
      container.addEventListener('touchstart', () => handleInteractionStart('touch'), { passive: true });
      container.addEventListener('touchend', () => handleInteractionEnd('touch', 500), { passive: true });
      container.addEventListener('focusin', () => handleInteractionStart('focus'));
      container.addEventListener('focusout', () => handleInteractionEnd('focus', 200));
    }

    // FIXED: Slide click events with drag detection
    // Skip for marquee mode - clicking slides doesn't make sense for continuous scroll
    if (mode !== 'marquee') {
      slides.forEach((slide, i) => {
        slide.addEventListener('click', (e) => {
          const movementThreshold = 5;
          const hasMovement = Math.abs(e.movementX || 0) > movementThreshold || Math.abs(e.movementY || 0) > movementThreshold;
          
          // Only handle clicks if not dragging and no significant movement
          if (!isDragging && !hasMovement) {
            debugLog('Slide clicked:', i);
            sliderInstance.goToSlide(i);

            if (mergedAutoplayConfig.pauseOnInteraction) {
              setUserInteracting(true, 'click');
              const clickDelay = Math.max(1000, mergedAutoplayConfig.delay * 0.3);
              setInteractionEndDelayed(clickDelay, 'click');
            }
          }
        });
      });
    }
  };

  // Enhanced visibility observer setup
  const setupVisibilityObserver = () => {
    if (!mergedAutoplayConfig.pauseOnInvisible) return;

    debugLog('Setting up visibility detection');

    visibilityController = createVisibilityDetection({
      container: container,
      onVisibilityChange: (visible) => {
        isContainerVisible = visible;
        debugLog("Container visibility changed:", visible);
        updateAutoplayState();
      },
      debug: debug
    });

    isPageVisible = !document.hidden;

    const handlePageVisibilityChange = () => {
      const wasPageVisible = isPageVisible;
      isPageVisible = !document.hidden;

      if (wasPageVisible !== isPageVisible) {
        debugLog("Page visibility changed:", isPageVisible ? "visible" : "hidden");
        updateAutoplayState();
      }
    };

    document.addEventListener('visibilitychange', handlePageVisibilityChange);

    container._pageVisibilityCleanup = () => {
      document.removeEventListener('visibilitychange', handlePageVisibilityChange);
    };
  };

  // Responsive handling
  const shouldActivateSlider = () => {
    if (!mergedResponsive.enabled) return true;
    if (!mergedResponsive.activeBelow && !mergedResponsive.activeAbove) return true;

    const width = window.innerWidth;
    let shouldActivate = true;

    if (mergedResponsive.activeBelow) {
      shouldActivate = shouldActivate && width <= mergedResponsive.activeBelow;
    }

    if (mergedResponsive.activeAbove) {
      shouldActivate = shouldActivate && width >= mergedResponsive.activeAbove;
    }

    debugLog('shouldActivateSlider check:', {
      width,
      activeBelow: mergedResponsive.activeBelow,
      activeAbove: mergedResponsive.activeAbove,
      shouldActivate,
      currentlyActive: isActive
    });

    return shouldActivate;
  };

  const handleResponsiveChange = () => {
    const shouldActivate = shouldActivateSlider();

    debugLog('handleResponsiveChange:', {
      shouldActivate,
      isActive,
      width: window.innerWidth
    });

    if (shouldActivate && !isActive) {
      debugLog('Activating slider due to responsive change');
      activateSlider();
    } else if (!shouldActivate && isActive) {
      debugLog('Deactivating slider due to responsive change');
      deactivateSlider();
    }
  };

  const setupResponsiveMonitoring = () => {
    if (!mergedResponsive.enabled) return;

    debugLog('Setting up responsive monitoring', {
      activeBelow: mergedResponsive.activeBelow,
      activeAbove: mergedResponsive.activeAbove
    });

    if (mergedResponsive.activeBelow) {
      const belowMq = window.matchMedia(`(max-width: ${mergedResponsive.activeBelow}px)`);
      const aboveMq = window.matchMedia(`(min-width: ${mergedResponsive.activeBelow + 1}px)`);

      const belowHandler = (e) => {
        debugLog('Below threshold media query changed:', e.matches);
        handleResponsiveChange();
      };

      const aboveHandler = (e) => {
        debugLog('Above threshold media query changed:', e.matches);
        handleResponsiveChange();
      };

      belowMq.addEventListener('change', belowHandler);
      aboveMq.addEventListener('change', aboveHandler);

      mmContext.push(
        { mq: belowMq, handler: belowHandler },
        { mq: aboveMq, handler: aboveHandler }
      );
    }

    if (mergedResponsive.activeAbove) {
      const aboveMq = window.matchMedia(`(min-width: ${mergedResponsive.activeAbove}px)`);
      const belowMq = window.matchMedia(`(max-width: ${mergedResponsive.activeAbove - 1}px)`);

      const activeAboveHandler = (e) => {
        debugLog('Active above media query changed:', e.matches);
        handleResponsiveChange();
      };

      const activeBelowHandler = (e) => {
        debugLog('Active below media query changed:', e.matches);
        handleResponsiveChange();
      };

      const existingAbove = mmContext.find(item => item.mq === aboveMq);
      const existingBelow = mmContext.find(item => item.mq === belowMq);

      if (!existingAbove) {
        aboveMq.addEventListener('change', activeAboveHandler);
        mmContext.push({ mq: aboveMq, handler: activeAboveHandler });
      }

      if (!existingBelow) {
        belowMq.addEventListener('change', activeBelowHandler);
        mmContext.push({ mq: belowMq, handler: activeBelowHandler });
      }
    }

    const resizeHandler = () => {
      clearTimeout(window._sliderResizeTimeout);
      window._sliderResizeTimeout = setTimeout(() => {
        debugLog('Window resized, checking responsive conditions');
        handleResponsiveChange();
      }, 150);
    };

    window.addEventListener('resize', resizeHandler);

    container._responsiveCleanup = () => {
      window.removeEventListener('resize', resizeHandler);
      clearTimeout(window._sliderResizeTimeout);
    };
  };

  // Streamlined deactivation
  const deactivateSlider = () => {
    if (!isActive) return;

    debugLog('Deactivating slider');

    // FIXED: Clear all interaction timeouts
    clearInteractionTimeout();
    stopAutoplay();

    if (loop) {
      debugLog('Destroying loop instance with complete cleanup');

      if (typeof loop.destroy === 'function') {
        loop.destroy();
      } else if (typeof loop.kill === 'function') {
        debugLog('destroy() method not available, using kill() as fallback');
        loop.kill();
      }

      loop = null;
    }

    debugLog('Killing any remaining GSAP animations');
    gsap.killTweensOf(slides);
    gsap.killTweensOf(container);

    gsap.set(slides, { clearProps: "all" });
    gsap.set(container, { clearProps: "all" });

    if (visibilityController) {
      debugLog('Destroying visibility controller');
      visibilityController.destroy();
      visibilityController = null;
    }

    if (container._pageVisibilityCleanup) {
      debugLog('Cleaning up page visibility listeners');
      container._pageVisibilityCleanup();
      delete container._pageVisibilityCleanup;
    }

    if (mode === 'stack') {
      debugLog('Cleaning up stack mode');

      // Clean up Ken Burns timelines
      cleanupKenBurns();

      if (container._stackDraggable) {
        container._stackDraggable.kill();
        delete container._stackDraggable;
      }

      if (container._dragOverlay && container._dragOverlay.parentNode) {
        container._dragOverlay.parentNode.removeChild(container._dragOverlay);
        delete container._dragOverlay;
      }
    }

    container.classList.remove('slider-active');

    if (mode === 'stack') {
      container.style.position = '';
      container.style.overflow = '';
    }

    // FIXED: Reset all state variables
    isActive = false;
    currentIndex = 0;
    userInteracting = false;
    isDragging = false;
    dragStartTime = 0;

    debugLog('Slider deactivated successfully', {
      isActive,
      hasLoop: !!loop
    });
  };

  // Main activation/deactivation
  const activateSlider = () => {
    if (isActive) return;

    debugLog('Activating slider');

    try {
      debugLog('Mode check:', mode, 'mode === stack:', mode === 'stack');

      if (mode === 'stack') {
        debugLog('Entering stack mode setup');

        setupStackMode();

        loop = {
          current: () => currentIndex,
          next: (vars = {}) => {
            const nextIndex = (currentIndex + 1) % slides.length;
            if (animateStackTransition(currentIndex, nextIndex, 1) === false) return;
            currentIndex = nextIndex;
            dispatchSlideChangeEvent(currentIndex);
          },
          previous: (vars = {}) => {
            const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
            if (animateStackTransition(currentIndex, prevIndex, -1) === false) return;
            currentIndex = prevIndex;
            dispatchSlideChangeEvent(currentIndex);
          },
          toIndex: (index, vars = {}) => {
            if (index !== currentIndex) {
              if (animateStackTransition(currentIndex, index) === false) return;
              currentIndex = index;
              dispatchSlideChangeEvent(currentIndex);
            }
          },
          pause: () => { },
          resume: () => { },
          kill: () => {
            if (container._stackDraggable) {
              container._stackDraggable.kill();
              delete container._stackDraggable;
            }

            if (container._dragOverlay && container._dragOverlay.parentNode) {
              container._dragOverlay.parentNode.removeChild(container._dragOverlay);
              delete container._dragOverlay;
            }
          },
          refresh: () => {
            setupStackMode();
          }
        };

        setupStackDragging();
      } else {
        debugLog('Entering standard mode setup for:', mode);
        const loopConfig = getModeConfig();

        loop = horizontalLoop(slides, loopConfig);

        if (!loop) {
          throw new Error("Failed to create horizontal loop");
        }

        // For marquee mode, horizontalLoop handles reversed state internally
        // Just need to ensure it's playing (not paused)
        if (mode === 'marquee') {
          // horizontalLoop already starts playing if paused: false
          // and handles reversed direction internally
          debugLog('Marquee started', { reversed: defaultMarqueeSettings.reverse });
        } else if (mergedAutoplayConfig.enabled) {
          loop.play();
        }

        if (center) {
          loop.toIndex(0, { duration: 0 });
        }
      }

      setupInteractionListeners();
      setupVisibilityObserver();

      isActive = true;
      container.classList.add('slider-active');

      if (animationDelay > 0) {
        debugLog(`Applying animation delay: ${animationDelay}s`);
        setTimeout(() => {
          if (mode !== 'marquee') {
            updateAutoplayState();
          }
        }, animationDelay * 1000);
      } else {
        if (mode !== 'marquee') {
          updateAutoplayState();
        }
      }

      debugLog('Slider activated successfully');
    } catch (error) {
      console.error("Failed to activate slider:", error);
    }
  };

  // Initialize with proper responsive checking
  const initializeSlider = () => {
    debugLog('Initializing slider with responsive settings:', mergedResponsive);

    if (mergedResponsive.enabled) {
      setupResponsiveMonitoring();

      const shouldActivate = shouldActivateSlider();
      debugLog('Initial responsive check:', { shouldActivate });

      if (shouldActivate) {
        activateSlider();
      } else {
        debugLog('Slider not activated due to responsive conditions');
      }
    } else {
      activateSlider();
    }
  };

  // Enhanced cleanup
  const destroySlider = () => {
    debugLog("Destroying slider");

    // FIXED: Clear interaction timeouts during destroy
    clearInteractionTimeout();
    stopAutoplay();

    mmContext.forEach(({ mq, handler }) => {
      mq.removeEventListener('change', handler);
    });
    mmContext.length = 0;

    if (container._responsiveCleanup) {
      container._responsiveCleanup();
      delete container._responsiveCleanup;
    }

    if (isActive) {
      deactivateSlider();
    }

    // FIXED: Reset all state variables
    userInteracting = false;
    isContainerVisible = true;
    isPageVisible = true;
    currentIndex = 0;
    isDragging = false;
    dragStartTime = 0;
  };

  // Public API
  const sliderInstance = {
    // FIXED: Navigation methods with proper interaction handling
    goToNext: () => {
      if (!loop) return;
      const wasAutoplay = autoplayInterval !== null;
      if (wasAutoplay) stopAutoplay();

      // FIXED: Set interaction state for manual navigation
      clearInteractionTimeout();
      setUserInteracting(true, 'manual-next');

      if (mode === 'marquee') {
        loop.next({ duration: 0 });
      } else {
        loop.next({ duration: duration, ease: easing });
      }

      // FIXED: Resume autoplay with proper delay
      if (wasAutoplay && mergedAutoplayConfig.enabled && mergedAutoplayConfig.resumeOnLeave) {
        const resumeDelay = Math.max(duration * 1000, mergedAutoplayConfig.delay * 0.5);
        setInteractionEndDelayed(resumeDelay, 'manual-next');
      }
    },

    goToPrevious: () => {
      if (!loop) return;
      const wasAutoplay = autoplayInterval !== null;
      if (wasAutoplay) stopAutoplay();

      // FIXED: Set interaction state for manual navigation
      clearInteractionTimeout();
      setUserInteracting(true, 'manual-previous');

      if (mode === 'marquee') {
        loop.previous({ duration: 0 });
      } else {
        loop.previous({ duration: duration, ease: easing });
      }

      // FIXED: Resume autoplay with proper delay
      if (wasAutoplay && mergedAutoplayConfig.enabled && mergedAutoplayConfig.resumeOnLeave) {
        const resumeDelay = Math.max(duration * 1000, mergedAutoplayConfig.delay * 0.5);
        setInteractionEndDelayed(resumeDelay, 'manual-previous');
      }
    },

    goToSlide: (index) => {
      if (!loop) return;
      const wasAutoplay = autoplayInterval !== null;
      if (wasAutoplay) stopAutoplay();

      // FIXED: Set interaction state for manual navigation
      clearInteractionTimeout();
      setUserInteracting(true, 'manual-goto');

      if (mode === 'marquee') {
        loop.toIndex(index, { duration: 0 });
      } else {
        loop.toIndex(index, { duration: duration, ease: easing });
      }

      // FIXED: Resume autoplay with proper delay
      if (wasAutoplay && mergedAutoplayConfig.enabled && mergedAutoplayConfig.resumeOnLeave) {
        const resumeDelay = Math.max(duration * 1000, mergedAutoplayConfig.delay * 0.5);
        setInteractionEndDelayed(resumeDelay, 'manual-goto');
      }
    },

    // State methods
    currentIndex: () => loop ? loop.current() : currentIndex,
    totalSlides: slides.length,
    isActive: () => isActive,
    mode: () => mode,

    // Lifecycle methods
    refresh: () => {
      if (loop && loop.refresh) {
        loop.refresh(true);
      }

      if (mode === 'stack') setupStackMode();
    },

    pause: () => {
      if (loop) loop.pause();
      stopAutoplay();
    },

    resume: () => {
      if (loop) loop.resume();
      updateAutoplayState();
    },

    destroy: destroySlider,

    // Autoplay control methods
    startAutoplay: () => {
      mergedAutoplayConfig.enabled = true;
      updateAutoplayState();
      return sliderInstance;
    },

    stopAutoplay: () => {
      stopAutoplay();
      return sliderInstance;
    },

    isAutoplayActive: () => autoplayInterval !== null,

    autoplayDelay: mergedAutoplayConfig.delay,

    restartAutoplay: () => {
      // Clear interaction state and restart autoplay cycle
      // Don't advance immediately since caller likely just changed slides
      clearInteractionTimeout();
      userInteracting = false;
      stopAutoplay();
      autoplayWasPaused = false; // Reset so we don't double-advance
      if (mergedAutoplayConfig.enabled) {
        startAutoplay(false);
      }
      return sliderInstance;
    },

    setAutoplayDelay: (delay) => {
      mergedAutoplayConfig.delay = delay;
      if (autoplayInterval) {
        stopAutoplay();
        updateAutoplayState();
      }

      return sliderInstance;
    },

    setAutoplayDirection: (direction) => {
      mergedAutoplayConfig.direction = direction;
      return sliderInstance;
    },

    // Mode-specific methods
    getLoop: () => loop,

    // Marquee-specific methods
    setSpeed: (newSpeed) => {
      if (mode === 'marquee' && loop) {
        loop.timeScale(defaultMarqueeSettings.reverse ? -newSpeed : newSpeed);
      }

      return sliderInstance;
    },

    reverseDirection: () => {
      if (mode === 'marquee' && loop) {
        defaultMarqueeSettings.reverse = !defaultMarqueeSettings.reverse;
        loop.reverse();
        debugLog('Marquee direction reversed:', defaultMarqueeSettings.reverse);
      }

      return sliderInstance;
    },

    // Enhanced settings methods
    setDragThreshold: (threshold) => {
      dragThreshold = threshold;
      debugLog('Drag threshold updated:', threshold);
      return sliderInstance;
    },

    setStackTransitionStyle: (style) => {
      if (mode === 'stack') {
        defaultStackSettings.transitionStyle = style;
        debugLog('Stack transition style updated:', style);
      }

      return sliderInstance;
    },

    setAnimationDelay: (delay) => {
      animationDelay = delay;
      debugLog('Animation delay updated:', delay);
      return sliderInstance;
    },

    setAnimationStagger: (stagger) => {
      animationStagger = stagger;
      debugLog('Animation stagger updated:', stagger);
      return sliderInstance;
    },

    // FIXED: Debug methods for troubleshooting
    getInteractionState: () => ({
      userInteracting,
      isDragging,
      isContainerVisible,
      isPageVisible,
      autoplayActive: autoplayInterval !== null
    }),

    forceResumeAutoplay: () => {
      debugLog('Forcing autoplay resume');
      clearInteractionTimeout();
      setUserInteracting(false, 'force-resume');
      return sliderInstance;
    }
  };

  // Create controls and progress bar
  if (typeof createSliderControls === 'function') {
    createSliderControls(containerSelector, sliderInstance);
  }

  if (typeof createSliderProgressBar === 'function') {
    createSliderProgressBar(containerSelector, sliderInstance);
  }

  // Register slider in global registry
  if (typeof registerSlider === 'function') {
    registerSlider(sliderInstance);
  } else {
    if (!window.sliderInstances) {
      window.sliderInstances = [];
    }

    window.sliderInstances.push(sliderInstance);
  }

  // Initialize
  initializeSlider();

  return sliderInstance;
}

// Convenience functions for backward compatibility
export function createLinearSlider(options = {}) {
  return createSlider({ ...options, mode: 'linear' });
}

export function createLoopSlider(options = {}) {
  return createSlider({ ...options, mode: 'loop' });
}

export function createMarqueeSlider(options = {}) {
  return createSlider({ ...options, mode: 'marquee' });
}

export function createStackSlider(options = {}) {
  return createSlider({ ...options, mode: 'stack' });
}